import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { testsService } from '@/services/tests.service';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { Save, X } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

export function TestForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    test_id: '',
    test_name: '',
    course_id: '',
    exam_id: '',
    test_type: 'practice_test' as 'mock_exam' | 'practice_test' | 'module_test' | 'diagnostic' | 'final_exam',
    total_questions: 20,
    duration_minutes: 60,
    passing_score_percent: 70,
    instructions: '',
    is_active: true,
    order_index: 0,
  });

  // Fetch exams
  const { data: exams = [] } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams({ status: 'active' });
      } catch (error) {
        return [];
      }
    },
  });

  // Fetch courses
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        return await coursesService.getCourses();
      } catch (error) {
        return [];
      }
    },
  });

  // Fetch test if editing
  const { data: test } = useQuery({
    queryKey: ['test', id],
    queryFn: async () => {
      try {
        return await testsService.getTestById(id!);
      } catch (error) {
        return null;
      }
    },
    enabled: isEditing,
  });

  useEffect(() => {
    if (test) {
      setFormData({
        test_id: test.test_id,
        test_name: test.test_name,
        course_id: test.course_id,
        exam_id: test.exam_id,
        test_type: test.test_type,
        total_questions: test.total_questions,
        duration_minutes: test.duration_minutes,
        passing_score_percent: test.passing_score_percent,
        instructions: test.instructions || '',
        is_active: test.is_active,
        order_index: test.order_index,
      });
    }
  }, [test]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await testsService.createTest(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tests'] });
      showToast('success', 'Test Created', `Test "${data.test_name}" has been created successfully.`);
      navigate('/tests');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to create test';
      showToast('error', 'Creation Failed', message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await testsService.updateTest(id!, data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tests'] });
      queryClient.invalidateQueries({ queryKey: ['test', id] });
      showToast('success', 'Test Updated', `Test "${data.test_name}" has been updated successfully.`);
      navigate('/tests');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to update test';
      showToast('error', 'Update Failed', message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Test' : 'Create Test'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test ID *
              </label>
              <input
                type="text"
                required
                value={formData.test_id}
                onChange={(e) => handleChange('test_id', e.target.value)}
                disabled={isEditing}
                className="input-field"
                placeholder="CPA-MOCK-1"
              />
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">Test ID cannot be changed</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test Name *
              </label>
              <input
                type="text"
                required
                value={formData.test_name}
                onChange={(e) => handleChange('test_name', e.target.value)}
                className="input-field"
                placeholder="Mock Exam 1"
              />
            </div>
          </div>
        </div>

        {/* Associations */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Associations</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam *
              </label>
              <select
                required
                value={formData.exam_id}
                onChange={(e) => handleChange('exam_id', e.target.value)}
                className="input-field"
              >
                <option value="">Select Exam</option>
                {exams.map(exam => (
                  <option key={exam.exam_id} value={exam.exam_id}>
                    {exam.exam_id} - {exam.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course *
              </label>
              <select
                required
                value={formData.course_id}
                onChange={(e) => handleChange('course_id', e.target.value)}
                className="input-field"
              >
                <option value="">Select Course</option>
                {courses.map(course => (
                  <option key={course.course_id} value={course.course_id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Test Configuration */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Configuration</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Type *
                </label>
                <select
                  required
                  value={formData.test_type}
                  onChange={(e) => handleChange('test_type', e.target.value)}
                  className="input-field"
                >
                  <option value="practice_test">Practice Test</option>
                  <option value="mock_exam">Mock Exam</option>
                  <option value="module_test">Module Test</option>
                  <option value="diagnostic">Diagnostic</option>
                  <option value="final_exam">Final Exam</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Index
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.order_index}
                  onChange={(e) => handleChange('order_index', parseInt(e.target.value))}
                  className="input-field"
                />
                <p className="text-xs text-gray-500 mt-1">Display order in course</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Questions *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.total_questions}
                  onChange={(e) => handleChange('total_questions', parseInt(e.target.value))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes) *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.duration_minutes}
                  onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passing Score (%) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  max="100"
                  value={formData.passing_score_percent}
                  onChange={(e) => handleChange('passing_score_percent', parseInt(e.target.value))}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructions
              </label>
              <textarea
                rows={4}
                value={formData.instructions}
                onChange={(e) => handleChange('instructions', e.target.value)}
                className="input-field"
                placeholder="Test instructions for students..."
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="w-4 h-4 text-primary-600"
              />
              <label className="ml-2 text-sm text-gray-700">
                Active (available to students)
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/tests')}
            className="btn-secondary flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isPending ? 'Saving...' : isEditing ? 'Update Test' : 'Create Test'}
          </button>
        </div>
      </form>
    </div>
  );
}
